import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  IconButton,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import MatchDetail from '@/components/MatchDetail';
import { Match, MatchStatus, Property, Client } from '@/types';
import { MatchingService } from '@/services/matchingService';
import { PropertyService } from '@/services/propertyService';
import { ClientService } from '@/services/clientService';

const MatchViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [property, setProperty] = useState<Property | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Match ID is required');
      setLoading(false);
      return;
    }

    const loadMatch = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load match
        const matchData = await MatchingService.getMatch(id);
        if (!matchData) {
          setError('Match not found');
          setLoading(false);
          return;
        }
        setMatch(matchData);

        // Load property
        try {
          const propertyData = await PropertyService.getProperty(matchData.propertyId);
          if (propertyData) {
            setProperty(propertyData);
          }
        } catch (err) {
          console.error('Failed to load property:', err);
        }

        // Load client
        try {
          const clientData = await ClientService.getClient(matchData.clientId);
          if (clientData) {
            setClient(clientData);
          }
        } catch (err) {
          console.error('Failed to load client:', err);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load match');
      } finally {
        setLoading(false);
      }
    };

    loadMatch();
  }, [id]);

  const handleStatusChange = async (match: Match, newStatus: MatchStatus) => {
    try {
      await MatchingService.updateMatchStatus(match.id, newStatus);
      // Update local state
      setMatch({ ...match, status: newStatus });
    } catch (err: any) {
      alert(`Failed to update match status: ${err.message}`);
    }
  };

  const handleViewProperty = (property: Property) => {
    navigate(`/properties/${property.id}`);
  };

  const handleViewClient = (client: Client) => {
    navigate(`/clients/${client.id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !match) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Match not found'}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/matches')}
        >
          Back to Matches
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/matches')} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
      </Box>

      <MatchDetail
        match={match}
        property={property || undefined}
        client={client || undefined}
        onStatusChange={handleStatusChange}
        onViewProperty={handleViewProperty}
        onViewClient={handleViewClient}
      />
    </Box>
  );
};

export default MatchViewPage;

